import {
  createRootRoute,
  createRoute,
  createRouter,
  Navigate,
  Outlet,
  useNavigate,
} from '@tanstack/react-router';
import { HomePage } from '@/pages/home';
import { DashboardEntry, entryFarmSearch } from '@/pages/dashboard/entry';
import { FieldsListPage } from '@/pages/fields/list';
import { fieldsListSearch } from '@/pages/fields/listSearch';
import { FieldDetailPage } from '@/pages/fields/detail';
import { FarmCardPage } from '@/pages/structure/farm-card';
import {
  FarmDataRedirect,
  FarmEntryPage,
  farmDataSearch,
  legacyFarmSearch,
} from '@/pages/farm';
import { SeasonClosingPage } from '@/pages/seasons/closing';
import { PlanningHubPage } from '@/pages/planning';
import { PlanningSalePricesPage } from '@/pages/planning/sale-prices';
import { BudgetCatalogsPage } from '@/pages/planning/budget-catalogs';
import { BudgetTargetsPage } from '@/pages/planning/budget-targets';
import { TechCardsPage } from '@/pages/planning/tech-cards';
import { PlanVariantPage } from '@/pages/planning/plan-variant';
import { PlanVariantFeasibilityPage } from '@/pages/planning/plan-variant/feasibility';
import { PlanVariantSummaryPage } from '@/pages/planning/plan-variant/summary';
import { ProgramComparePage } from '@/pages/planning/program-compare';
import { CropStructurePage } from '@/pages/planning/spp';
import { SppOverviewPage } from '@/pages/planning/spp-overview';
import { SowingPlanPage } from '@/pages/planning/sowing-plan';
import { SystemsLibraryPage } from '@/pages/planning/systems';
import { SystemEditorPage } from '@/pages/planning/systems/editor';
import { ReadinessPage } from '@/pages/planning/readiness';
import { YieldNormsPage } from '@/pages/planning/yield-norms';
import { SeasonBudgetHubPage } from '@/pages/planning/season-budget';
import { HistoryAnalyticsPage } from '@/pages/analytics/history';
import { MapPage } from '@/pages/map';
import { LoginPage } from '@/pages/auth/login';
import { TwoFARequiredPage } from '@/pages/auth/2fa-required';
import { TwoFAChallengePage } from '@/pages/auth/2fa-challenge';
import { SelectTenantPage } from '@/pages/auth/select-tenant';
import { ForgotPasswordPage } from '@/pages/auth/forgot-password';
import { ResetPasswordPage } from '@/pages/auth/reset-password';
import { AuthErrorPage } from '@/pages/auth/error';
import { LinkSentPage } from '@/pages/auth/link-sent';
import { AcceptInvitePage } from '@/pages/accept-invite';
import { TenantSuspendedPage } from '@/pages/tenant-suspended';
import { OperationsListPage } from '@/pages/operations/list';
import { OperationDetailPage } from '@/pages/operations/detail';
import { SciencePage, scienceSearch } from '@/pages/science';
import { TrialSchemePage } from '@/pages/science/scheme';
import { TrialSchemeFormPage } from '@/pages/science/scheme-form';
import { PlaceTrialPage, placeSearch } from '@/pages/science/place';
import { TrialCardPage } from '@/pages/science/trial';
import { OnboardingCompanyPage } from '@/pages/onboarding/company';
import { SetPasswordOnboardingPage } from '@/pages/onboarding/set-password';
import { FirstRunWizardPage } from '@/pages/onboarding/first-run';
import { GettingStartedPage } from '@/pages/getting-started';
import { ConsultantProtosPage } from '@/pages/consultant/protos';
import { PlatformAdminDashboardPage } from '@/pages/platform-admin/Dashboard';
import { PlatformAdminCompaniesPage } from '@/pages/platform-admin/Companies';
import { PlatformAdminUsersPage } from '@/pages/platform-admin/Users';
import { PlatformAdminAdminsPage } from '@/pages/platform-admin/Admins';
import { PlatformAdminCropsPage } from '@/pages/platform-admin/reference/Crops';
import { PlatformAdminCropCategoriesPage } from '@/pages/platform-admin/reference/Categories';
import { PlatformAdminCropGroupsPage } from '@/pages/platform-admin/reference/CropGroups';
import { PlatformAdminGrowingGoalsPage } from '@/pages/platform-admin/reference/GrowingGoals';
import { PlatformAdminDiseasesPage } from '@/pages/platform-admin/reference/Diseases';
import { PlatformAdminPestsPage } from '@/pages/platform-admin/reference/Pests';
import { PlatformAdminWeedsPage } from '@/pages/platform-admin/reference/Weeds';
import { PlatformAdminDiseaseCategoriesPage } from '@/pages/platform-admin/reference/DiseaseCategories';
import { PlatformAdminFieldStatesPage } from '@/pages/platform-admin/reference/FieldStates';
import { PlatformAdminLandCategoriesPage } from '@/pages/platform-admin/reference/LandCategories';
import { PlatformAdminPestCategoriesPage } from '@/pages/platform-admin/reference/PestCategories';
import { PlatformAdminWeedCategoriesPage } from '@/pages/platform-admin/reference/WeedCategories';
import { PlatformAdminWeedLifeCyclesPage } from '@/pages/platform-admin/reference/WeedLifeCycles';
import { PlatformAdminOperationTypesPage } from '@/pages/platform-admin/reference/OperationTypes';
import { PlatformAdminMachineryPage } from '@/pages/platform-admin/reference/Machinery';
import { PlatformAdminMachineryTypesPage } from '@/pages/platform-admin/reference/MachineryTypes';
import { PlatformAdminManufacturersPage } from '@/pages/platform-admin/reference/Manufacturers';
import { PlatformAdminUnitsPage } from '@/pages/platform-admin/reference/Units';
import { PlatformAdminPesticidesPage } from '@/pages/platform-admin/reference/pesticides/PesticidesAdminPage';
import { PlatformAdminCropQueuePage } from '@/pages/platform-admin/reference/crop-queue/CropImportQueuePage';
import { PlatformAdminVarietiesPage } from '@/pages/platform-admin/reference/varieties/VarietiesPage';
import { PesticidesCatalogPage } from '@/pages/reference/pesticides/PesticidesCatalogPage';
import { CompanyPage, CompanyTabPage } from '@/pages/company';
import { CrmListPage } from '@/pages/crm/list';
import { CrmNewPage } from '@/pages/crm/new';
import { CrmDetailPage } from '@/pages/crm/detail';
import { ConsultantClientsPage } from '@/pages/consultant/clients';
import { ConsultantClientCardPage } from '@/pages/consultant/clientCard';
import { parseClientCardTab, type ClientCardTab } from '@/lib/consultant/clientCard';
import {
  RouteErrorFallback,
  NotFoundFallback,
} from '@/components/layout/RouteErrorFallback';

/**
 * TanStack Router — code-based routing.
 *
 * Phase 5B добавляет /login, /auth/2fa-required, /accept-invite,
 * /tenant-suspended, /operations, /onboarding/company, /getting-started.
 *
 * Auth-страницы — standalone (без AppShell внутри). Operations / getting-started —
 * с AppShell. Onboarding/company — light shell (свой top-bar без sidebar).
 *
 * Phase 5A (Agent A) параллельно добавит /fields, /fields/$fieldId,
 * /fields/new, /map — мерж в router при их commit.
 */

const rootRoute = createRootRoute({
  component: () => <Outlet />,
  errorComponent: RouteErrorFallback,
  notFoundComponent: NotFoundFallback,
});

// `/?farm=<id>` — присланная ссылка на хозяйство (AK-896, UC-7): `HomePage`
// доносит её до `/dashboard` как есть.
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: function IndexRouteComponent() {
    const { farm } = indexRoute.useSearch();
    return <HomePage farmSearch={farm} />;
  },
  validateSearch: entryFarmSearch,
});

// AK-898 / AK-896 — вход по правилу области и заполненности двойника
// (макет v2 экран 3): «Холдинг» → экран «Холдинг» (нынешний дашборд),
// хозяйство → «Данные хозяйства» (двойник не заполнен) или «Карта» (заполнен),
// кластер → `/farm` (просьба выбрать). Какое хозяйство — ссылка `?farm=` →
// сессия → последнее выбранное → единственное → «Холдинг» → первое по
// алфавиту; правило и причины — `components/scope/resolveEntry.ts`,
// исполнение — `pages/dashboard/entry.tsx`. `/` → `/dashboard` → сюда.
// `?farm=` после переключения области убирается из адреса: дальше источник
// хозяйства один — переключатель.
const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboard',
  component: function DashboardRouteComponent() {
    const { farm } = dashboardRoute.useSearch();
    const navigate = useNavigate();
    return (
      <DashboardEntry
        farmSearch={farm}
        onDeepLinkApplied={() => void navigate({ to: '/dashboard', search: {}, replace: true })}
      />
    );
  },
  validateSearch: entryFarmSearch,
});

// AK-750 — фильтры списка полей живут в адресе (`?farm_id=&search=&crop=…`).
// Founder 25.08: «открыл поле — нажал назад — фильтры сохраняются». Плюс
// бесплатно: отфильтрованный список пересылается ссылкой. Схема и разбор
// значений — `pages/fields/listSearch` (там же грабля JSON-сериализации).
const fieldsListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/fields',
  component: FieldsListPage,
  validateSearch: fieldsListSearch,
});

// AK-368a — старый /fields/new (OSM + mapbox-gl-draw) выпилен. «Добавить поле»
// живёт на стандартной карте: редиректим на /map?addField=1 (закладки живы).
const fieldsNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/fields/new',
  component: function NewFieldRedirect() {
    return <Navigate to="/map" search={{ addField: 1 }} replace />;
  },
});

const fieldsDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/fields/$fieldId',
  component: FieldDetailPage,
});

// SA-01 фаза 1 — «Закрытие сезона»: подчистка статусов посевов в конце года.
// Не в сайдбаре: действие сезонное, точка входа — кнопка на экране «Поля».
// AK-434 / SA-03 — вход в раздел «Планирование сезона» (экран 10, список сезонов).
//
// `?company_id=<uuid>` — фиксация выбранной компании при переходе с вкладки
// «Настройки → Сезон» (AK-541 P1-2). Без неё пользователь, только что выбравший
// компанию на настройках в холдинге с несколькими юрлицами, снова попадал бы
// в селектор «выберите компанию» — не «Обновите страницу», но лишний клик.
// AK-434 / story #6 — v2 экрана /planning: единственный хаб раздела.
// Отдельные роуты /planning/seasons (список) и /planning/seasons/$year/settings
// (форма) сняты (revert #690): список избыточен — хаб уже показывает три
// секции; форма settings была дублем /company/season и создавала UX-ловушку
// (пользователь думает, что настраивает сезон 2004, а меняет глобальный шаблон).
const planningRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/planning',
  component: PlanningHubPage,
  validateSearch: (search: Record<string, unknown>): { company_id?: string } => ({
    company_id: typeof search.company_id === 'string' ? search.company_id : undefined,
  }),
});

// Хаб «Бюджет сезона {год}» — экран входа в разделы бюджета (макет v2-00-hub,
// решение founder 20.08: «по прототипам там был крутой экран», а не попап).
// Карточка сезона в `/planning` ведёт сюда; разделы — навигация внутри экрана.
const planningSeasonBudgetRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/planning/seasons/$year/budget',
  component: SeasonBudgetHubPage,
});

// «Обзор СПП» (AK-740) — drill-down компания → кластер → хозяйство, этап
// анализа ПЕРЕД планированием сезона (JTBD founder 23.08). Не годовой роут
// `/seasons/$year/...`: уровень адресуется query (`level`/`id`), а не годом —
// экран сравнивает НЕСКОЛЬКО лет факта + варианты будущего одновременно.
const planningSppOverviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/planning/spp-overview',
  component: SppOverviewPage,
  validateSearch: (
    search: Record<string, unknown>,
  ): { level?: 'company' | 'cluster' | 'farm'; id?: string; variants?: string } => ({
    level:
      search.level === 'company' || search.level === 'cluster' || search.level === 'farm'
        ? search.level
        : undefined,
    id: typeof search.id === 'string' && search.id ? search.id : undefined,
    // До 3 UUID через запятую — сравниваемые варианты будущего (уровень
    // хозяйства). URL — единственный источник правды (ревью head-web #892
    // п.20): ссылку «сравнение 1+3» кидают коллеге, и она обязана её открыть.
    variants: typeof search.variants === 'string' && search.variants ? search.variants : undefined,
  }),
});

// AK-Planning — форма плановых цен (спека 2026-08-06-spp-scenarios-v1 §1,
// прототип 2026-08-06-spp-scenarios экран 3). Самодостаточный роут, не
// вплетён в хаб `/planning` (нестабилен после revert #690) — резолвит
// сезон сам по году, как настройки сезона выше.
const planningSalePricesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/planning/seasons/$year/sale-prices',
  component: PlanningSalePricesPage,
});

// Бюджетные справочники сезона — экран 1 годового планирования (макет
// v2-01-budget-catalogs, утверждён founder 15.08). Сезон резолвится по году
// из url, как у плановых цен выше.
const planningBudgetCatalogsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/planning/seasons/$year/budget-catalogs',
  component: BudgetCatalogsPage,
});

// Цели сезона — декомпозиция целей УК: холдинг / кластеры / хозяйства
// (ADR-069, экран v2-06). Сезон резолвится по году из url, как у бюджетных
// справочников выше.
const planningBudgetTargetsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/planning/seasons/$year/budget-targets',
  component: BudgetTargetsPage,
});

// Техкарты-варианты сезона — экран 2(б) годового планирования (макет
// v2-05-techcard, утверждён founder 15.08; модель — ADR-066). Сезон резолвится
// по году из url, как у бюджетных справочников выше.
//
// `?card=<uuid>` — открытый вариант. В адресе он живёт намеренно: ссылку на
// конкретный вариант («посмотри интенсив») кидают в переписке, и она обязана
// открывать именно его, а не список.
const planningTechCardsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/planning/seasons/$year/tech-cards',
  component: TechCardsPage,
  validateSearch: (search: Record<string, unknown>): { card?: string; calc?: string } => ({
    card: typeof search.card === 'string' && search.card ? search.card : undefined,
    // Расчётный контекст (ADR-071 D1): вариант плана, на площадь которого
    // считаем деньги карты. Ссылка «карта на площади варианта» пересылаемая.
    calc: typeof search.calc === 'string' && search.calc ? search.calc : undefined,
  }),
});

// Вариант посевного плана: назначение техкарт (макет v2-04) и сводка варианта
// (макет v2-14), утверждены founder 18.08. Оба экрана адресуются годом сезона,
// как соседи по разделу.
//
// `?farm` / `?variant` — хозяйство и вариант плана. В адресе они живут
// намеренно: вариант плана ведётся по хозяйству, и ссылка «посмотри базовый»
// обязана открыть именно его, а не первый попавшийся.
// `?row` — выделенная строка плана, чтобы подбор варианта открывался на ней.
const planVariantSearch = (
  search: Record<string, unknown>,
): { farm?: string; variant?: string; row?: string } => ({
  farm: typeof search.farm === 'string' && search.farm ? search.farm : undefined,
  variant: typeof search.variant === 'string' && search.variant ? search.variant : undefined,
  row: typeof search.row === 'string' && search.row ? search.row : undefined,
});

const planVariantRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/planning/seasons/$year/plan-variant',
  component: PlanVariantPage,
  validateSearch: planVariantSearch,
});

// Этап 4 — проверка выполнимости варианта ПП (UC-4, макет 05-feasibility).
// Адресуется теми же `?farm` / `?variant`, что и соседи по разделу: «посмотри,
// хватает ли нам техники по варианту Б-2» пересылают ссылкой.
const planVariantFeasibilityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/planning/seasons/$year/plan-variant/feasibility',
  component: PlanVariantFeasibilityPage,
  validateSearch: planVariantSearch,
});

const planVariantSummaryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/planning/seasons/$year/plan-variant/summary',
  component: PlanVariantSummaryPage,
  validateSearch: planVariantSearch,
});

// Структура посевных площадей (СПП) — экран 2(в) годового планирования (макет
// v2-02, утверждён founder 18.08). СПП — СРЕЗ варианта плана, поэтому экран
// читающий, а хозяйство и вариант живут в адресе теми же именами, что у
// соседей по разделу (`planVariantSearch` выше): ссылку на «структуру варианта
// Больше подсолнечника» кидают в переписке, и она обязана открыть именно его.
// Сравнение вариантов производственной программы и утверждение бюджета —
// этап 5 планирования-2027 (UC-5, вайрфрейм 06-compare-approve, одобрен
// founder 26.08). Адрес сезонный, как у соседей по разделу; `?variant` — на
// какой колонке открыт экран решения: ссылку «смотри вариант Б-2, вот на нём
// цели сходятся» кидают в переписке, и она обязана открыть именно его.
const programCompareRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/planning/seasons/$year/programs/compare',
  component: ProgramComparePage,
  validateSearch: (search: Record<string, unknown>): { farm?: string; variant?: string } => ({
    farm: typeof search.farm === 'string' && search.farm ? search.farm : undefined,
    variant: typeof search.variant === 'string' && search.variant ? search.variant : undefined,
  }),
});

const planningCropStructureRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/planning/seasons/$year/crop-structure',
  component: CropStructurePage,
  validateSearch: (
    search: Record<string, unknown>,
  ): { farm?: string; variant?: string } => ({
    farm: typeof search.farm === 'string' && search.farm ? search.farm : undefined,
    variant:
      typeof search.variant === 'string' && search.variant ? search.variant : undefined,
  }),
});

// Посевной план варианта — распределение культур ПО ПОЛЯМ (AK-408, макет
// v2-15). Хозяйство и вариант зовутся в адресе так же, как у соседей по
// разделу: «покажи посевной план варианта X» кидают ссылкой, и она обязана
// открыть именно его.
//
// Фильтры (`prev` / `prev2` / `plan`) тоже живут в адресе: «вот эти поля после
// пшеницы» — то, что пересылают коллеге, и ссылка обязана открыть ту же
// выборку. Значения — id культур (строки), плюс служебные `unknown` («нет
// данных») и `none` («культура не назначена»). Числовых флагов здесь нет
// сознательно: TanStack сериализует search через JSON, и строка '1' вернулась
// бы как `%221%22` (грабля карты ниже) — id культуры такой проблемы не имеет.
// Готовность данных к планированию — этап 0 раздела (UC-0, AK-763; wireframe
// `docs/superpowers/prototypes/season-planning-2027/01-readiness.html`,
// одобрен founder 26.08).
//
// Адрес сезонный (`/planning/seasons/$year/…`), как у всех соседей раздела, а
// не «/planning/readiness»: чек-лист считается ПО СЕЗОНУ, и год в пути — то
// же самое, что у посевного плана, техкарт и бюджетных справочников. Экран без
// года пришлось бы резолвить «какой-нибудь» сезон, а такая ссылка у коллеги
// открывала бы уже другой.
//
// `?farm=` — хозяйство. В адресе намеренно: чек-лист считается по каждому
// хозяйству отдельно, и ссылку «посмотри, чего у Зимовников не хватает»
// пересылают в переписке.
const planningReadinessRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/planning/seasons/$year/readiness',
  component: ReadinessPage,
  validateSearch: (search: Record<string, unknown>): { farm?: string } => ({
    farm: typeof search.farm === 'string' && search.farm ? search.farm : undefined,
  }),
});

// Справочник нормативов урожайности (AK-789, макет одобрен founder 28.08).
//
// Адрес СЕЗОННЫЙ, как у соседей раздела: норматив бывает заведён «на сезон
// 2027» и «на любой сезон», и открывать справочник без года значило бы
// показывать лестницу, у которой не задана верхняя ступень.
//
// `?crop=` — предвыбор культуры: из посевного плана сюда приходят по ссылке
// «норматива нет: нут — завести», и человек должен попасть на нужную строку,
// а не искать её заново.
const planningYieldNormsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/planning/seasons/$year/yield-norms',
  component: YieldNormsPage,
  validateSearch: (
    search: Record<string, unknown>,
  ): { farm?: string; crop?: string; show?: 'all' | 'plan' | 'gaps' } => ({
    farm: typeof search.farm === 'string' && search.farm ? search.farm : undefined,
    crop: typeof search.crop === 'string' && search.crop ? search.crop : undefined,
    show:
      search.show === 'plan' || search.show === 'gaps' || search.show === 'all'
        ? search.show
        : undefined,
  }),
});

// Библиотека систем и редактор системы (AK-773, решение founder Р35): защита /
// питание / обработка почвы — самостоятельные мини-техкарты, которые
// вставляются в техкарту и заменяются целиком.
//
// Адрес НЕ сезонный (`/planning/systems`, а не `/planning/seasons/$year/...`):
// в библиотеку заходят «посмотреть свои системы», а не по ссылке на конкретный
// год. Год живёт необязательным `?year=` — без него открывается текущий сезон,
// со ссылкой «системы сезона 2027» открывается он.
const planningSystemsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/planning/systems',
  component: SystemsLibraryPage,
  validateSearch: (
    search: Record<string, unknown>,
  ): { year?: number; kind?: 'protection' | 'nutrition' | 'tillage'; crop?: string } => {
    const year = Number(search.year);
    const kind = search.kind;
    return {
      year: Number.isFinite(year) && year >= 2000 && year <= 2100 ? year : undefined,
      kind:
        kind === 'protection' || kind === 'nutrition' || kind === 'tillage' ? kind : undefined,
      crop: typeof search.crop === 'string' && search.crop ? search.crop : undefined,
    };
  },
});

const planningSystemEditorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/planning/systems/$moduleId',
  component: SystemEditorPage,
});

const planningSowingPlanRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/planning/seasons/$year/sowing-plan',
  component: SowingPlanPage,
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    farm?: string;
    variant?: string;
    prev?: string;
    prev2?: string;
    plan?: string;
  } => ({
    farm: typeof search.farm === 'string' && search.farm ? search.farm : undefined,
    variant:
      typeof search.variant === 'string' && search.variant ? search.variant : undefined,
    prev: typeof search.prev === 'string' && search.prev ? search.prev : undefined,
    prev2: typeof search.prev2 === 'string' && search.prev2 ? search.prev2 : undefined,
    plan: typeof search.plan === 'string' && search.plan ? search.plan : undefined,
  }),
});

// Аналитика прошлых лет (wireframe одобрен founder 07.08, решение А).
// Экран ценен сам по себе, поэтому у него СВОЙ раздел, а не только вход из
// планирования. Второй вход — из бюджета сезона — монтирует тот же компонент
// `HistoryAnalytics` с `context="planning"`.
//
// `?farm_id` / `?crop_id` — предвыбор для входа «смотреть» из строки СПП:
// открыть уже отфильтрованным на нужную культуру.
// `?from=planning` — тот же экран, но с крошками бюджета сезона и кнопкой
// «Взять в план»: подставлять число есть куда только когда пришли из плана.
const analyticsHistoryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/analytics/history',
  component: HistoryAnalyticsPage,
  validateSearch: (
    search: Record<string, unknown>,
  ): { farm_id?: string; crop_id?: string; company_id?: string; from?: 'planning' } => ({
    farm_id: typeof search.farm_id === 'string' ? search.farm_id : undefined,
    crop_id: typeof search.crop_id === 'string' ? search.crop_id : undefined,
    // Юрлицо из хаба планирования (AK-541): в холдинге без него «все
    // хозяйства» смешают урожайность разных компаний в одно число.
    company_id: typeof search.company_id === 'string' ? search.company_id : undefined,
    from: search.from === 'planning' ? 'planning' : undefined,
  }),
});

const seasonClosingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/season-closing',
  component: SeasonClosingPage,
});

const mapRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/map',
  component: MapPage,
  // AK-368a — режим добавления поля активируется на входе (?addField=1).
  // AK-369 — `?import=1` открывает загрузку файла сразу («одна дверь» импорта).
  // v3 IA (карточка поля): `?field=<id>&lng=&lat=` — «📍 На карте» из карточки,
  // центрирует карту на поле (flyTo по центроиду) и выделяет его.
  // AK-584 — `farm`/`cluster` (deep-link скоупа, AK-296) и `onboarding=1`
  // (вход из мастера первого входа) обязаны ПЕРЕЖИВАТЬ клиентскую навигацию:
  // всё, чего нет в этом списке, TanStack Router вырезает из URL, и карта
  // (она читает `window.location.search`) параметр уже не видит. Раньше
  // `?farm=` работал только при полной перезагрузке страницы.
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    addField?: 1;
    import?: 1;
    field?: string;
    lng?: number;
    lat?: number;
    farm?: string;
    cluster?: string;
    onboarding?: 1;
  } => {
    const lng = Number(search.lng);
    const lat = Number(search.lat);
    // AK-591: TanStack Router (де)сериализует query через JSON. Поэтому:
    //   - при ПОЛНОЙ загрузке `?addField=1` приходит ЧИСЛОМ 1 — прежнее
    //     строгое `=== '1'` выбрасывало параметр, router затирал URL
    //     (replaceState) раньше, чем карта прочитает window.location.search,
    //     и режим «+ Поле» не взводился вовсе;
    //   - СТРОКА '1' на выходе validateSearch сериализуется с JSON-кавычками
    //     (`addField=%221%22`) — и её не узнаёт `params.get(...) === '1'` в
    //     v2.tsx. Флаги живут ЧИСЛОМ 1: «1» ↔ 1 round-trip'ится без кавычек.
    const flag = (v: unknown): 1 | undefined =>
      v === '1' || v === 1 ? 1 : undefined;
    return {
      addField: flag(search.addField),
      import: flag(search.import),
      field: typeof search.field === 'string' ? search.field : undefined,
      lng: Number.isFinite(lng) ? lng : undefined,
      lat: Number.isFinite(lat) ? lat : undefined,
      farm: typeof search.farm === 'string' ? search.farm : undefined,
      cluster: typeof search.cluster === 'string' ? search.cluster : undefined,
      onboarding: flag(search.onboarding),
    };
  },
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
});

const twoFARequiredRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/2fa-required',
  component: TwoFARequiredPage,
});

const twoFAChallengeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/2fa',
  component: TwoFAChallengePage,
  validateSearch: (search: Record<string, unknown>): { token?: string } => ({
    token: typeof search.token === 'string' ? search.token : undefined,
  }),
});

// AK-660 — выбор кабинета. Роута не было вовсе: и вход, и 2FA-челлендж, и
// профиль слали сюда человека с несколькими кабинетами, а он упирался в 404 и
// оставался без активного кабинета (403 на всех рабочих экранах).
const selectTenantRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/select-tenant',
  component: SelectTenantPage,
});

// Развилка для администратора платформы (кабинеты + служебная часть) — тот же
// экран, плюс карточка «Администрирование платформы». Сюда шлёт вход, и до
// AK-660 это была такая же дыра в 404.
const selectContextRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/select-context',
  component: SelectTenantPage,
});

const acceptInviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/accept-invite',
  component: AcceptInvitePage,
});

const tenantSuspendedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/tenant-suspended',
  component: TenantSuspendedPage,
});

const operationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/operations',
  component: OperationsListPage,
});

// AK-525 — до сих пор роута не было: список операций и виджет карточки поля
// ссылались на /operations/{id}, который упирался в 404. Карточка операции —
// единственное место, где статусом и работами можно УПРАВЛЯТЬ, а не только
// смотреть.
const operationDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/operations/$opId',
  component: OperationDetailPage,
});

// AK-938 — раздел «Наука» (low-fi 21.10, утверждён founder, PR #1177): опыты на
// полях и схемы опытов компании. `?tab=schemes` — вкладка «Схемы».
const scienceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/science',
  validateSearch: scienceSearch,
  component: function ScienceRouteComponent() {
    return <SciencePage search={scienceRoute.useSearch()} />;
  },
});

const scienceSchemeNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/science/schemes/new',
  component: () => <TrialSchemeFormPage />,
});

const scienceSchemeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/science/schemes/$schemeId',
  component: function ScienceSchemeRouteComponent() {
    const { schemeId } = scienceSchemeRoute.useParams();
    return <TrialSchemePage key={schemeId} schemeId={schemeId} />;
  },
});

const scienceSchemeEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/science/schemes/$schemeId/edit',
  component: function ScienceSchemeEditRouteComponent() {
    const { schemeId } = scienceSchemeEditRoute.useParams();
    return <TrialSchemeFormPage key={schemeId} schemeId={schemeId} />;
  },
});

// «Заложить опыт» — `?scheme=&farm=&field=` предзаполняют шаги (вход из
// карточки схемы, поля или хозяйства).
const sciencePlaceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/science/place',
  validateSearch: placeSearch,
  component: function SciencePlaceRouteComponent() {
    return <PlaceTrialPage search={sciencePlaceRoute.useSearch()} />;
  },
});

// Карточка опыта читается в кабинете поля: адрес несёт поле и опыт.
const scienceTrialRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/science/trials/$fieldId/$trialId',
  component: function ScienceTrialRouteComponent() {
    const { fieldId, trialId } = scienceTrialRoute.useParams();
    return <TrialCardPage key={trialId} fieldId={fieldId} trialId={trialId} />;
  },
});

const onboardingCompanyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/onboarding/company',
  component: OnboardingCompanyPage,
});

const gettingStartedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/getting-started',
  component: GettingStartedPage,
});

const consultantProtosRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/consultant/protos',
  component: ConsultantProtosPage,
});

// Platform Admin (AK-216/217/218) — internal-root tooling for founder/staff.
// Auth-guard внутри PlatformAdminLayout (платформенная роль).
const platformAdminDashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/platform-admin',
  component: PlatformAdminDashboardPage,
});

const platformAdminCompaniesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/platform-admin/companies',
  component: PlatformAdminCompaniesPage,
});

// AK-572: пункты меню «Пользователи» и «Админы» существовали с самого
// начала и вели в 404 — маршрутов не было, хотя бэкенд готов целиком.
const platformAdminUsersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/platform-admin/users',
  component: PlatformAdminUsersPage,
});

const platformAdminAdminsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/platform-admin/admins',
  component: PlatformAdminAdminsPage,
});

// Platform Admin → Справочники (AK-231 Pilot — reference-CRUD эталон).
const platformAdminCropsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/platform-admin/reference/crops',
  component: PlatformAdminCropsPage,
});

const platformAdminCropCategoriesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/platform-admin/reference/crop-categories',
  component: PlatformAdminCropCategoriesPage,
});

const platformAdminCropGroupsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/platform-admin/reference/crop-groups',
  component: PlatformAdminCropGroupsPage,
});

const platformAdminGrowingGoalsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/platform-admin/reference/growing-goals',
  component: PlatformAdminGrowingGoalsPage,
});

// AK-232/233/234 — Болезни / Вредители / Сорняки + мини-справочники категорий.
const platformAdminDiseasesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/platform-admin/reference/diseases',
  component: PlatformAdminDiseasesPage,
});

const platformAdminPestsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/platform-admin/reference/pests',
  component: PlatformAdminPestsPage,
});

const platformAdminWeedsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/platform-admin/reference/weeds',
  component: PlatformAdminWeedsPage,
});

const platformAdminDiseaseCategoriesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/platform-admin/reference/disease-categories',
  component: PlatformAdminDiseaseCategoriesPage,
});

const platformAdminFieldStatesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/platform-admin/reference/field-states',
  component: PlatformAdminFieldStatesPage,
});

const platformAdminLandCategoriesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/platform-admin/reference/land-categories',
  component: PlatformAdminLandCategoriesPage,
});

const platformAdminPestCategoriesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/platform-admin/reference/pest-categories',
  component: PlatformAdminPestCategoriesPage,
});

const platformAdminWeedCategoriesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/platform-admin/reference/weed-categories',
  component: PlatformAdminWeedCategoriesPage,
});

const platformAdminWeedLifeCyclesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/platform-admin/reference/weed-life-cycles',
  component: PlatformAdminWeedLifeCyclesPage,
});

// AK-238 — Тех. операции (reference.operation_types).
const platformAdminOperationTypesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/platform-admin/reference/operation-types',
  component: PlatformAdminOperationTypesPage,
});

// AK-235 — Техника (machinery catalog).
const platformAdminMachineryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/platform-admin/reference/machinery',
  component: PlatformAdminMachineryPage,
});

const platformAdminMachineryTypesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/platform-admin/reference/machinery-types',
  component: PlatformAdminMachineryTypesPage,
});

const platformAdminManufacturersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/platform-admin/reference/manufacturers',
  component: PlatformAdminManufacturersPage,
});

const platformAdminUnitsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/platform-admin/reference/units',
  component: PlatformAdminUnitsPage,
});

// Разбор культур — платформенная очередь reference.crop_import_queue.
const platformAdminCropQueueRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/platform-admin/reference/crop-queue',
  component: PlatformAdminCropQueuePage,
});

// Сорта и гибриды (reference.varieties) — каталог + разбор очереди сортов.
const platformAdminVarietiesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/platform-admin/reference/varieties',
  component: PlatformAdminVarietiesPage,
});

// AK-385 — Пестициды (СЗР): системная админка (импорт / очередь / подсправочники).
const platformAdminPesticidesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/platform-admin/reference/pesticides',
  component: PlatformAdminPesticidesPage,
});

// AK-385 — Пестициды (СЗР): пользовательский справочник (3 вида поиска).
const pesticidesCatalogRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reference/pesticides',
  component: PesticidesCatalogPage,
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/forgot-password',
  component: ForgotPasswordPage,
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/reset-password',
  component: ResetPasswordPage,
});

// «Придумайте пароль» при первом входе по приглашению. next=… — куда идти
// после установки пароля (валидируется как относительный путь на странице).
const setPasswordOnboardingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/onboarding/set-password',
  component: SetPasswordOnboardingPage,
  validateSearch: (search: Record<string, unknown>): { next?: string } => ({
    next: typeof search.next === 'string' ? search.next : undefined,
  }),
});

// Тупики ссылок из писем. Бэкенд уводит сюда пять редиректами
// (`invitations.py` ×3, `auth.py` ×2), а маршрута не было — 404 заворачивался в
// AppShell, тот на 401 гнал на /login, и человек получал немой экран входа
// вместо объяснения (прод 21.08.2026, видно построчно в логе nginx).
const authErrorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/error',
  component: AuthErrorPage,
  validateSearch: (search: Record<string, unknown>): { reason?: string } => ({
    reason: typeof search.reason === 'string' ? search.reason : undefined,
  }),
});

// «Письмо отправлено» после заказа входа по ссылке — сюда уводит login.tsx.
const linkSentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/link-sent',
  component: LinkSentPage,
  validateSearch: (search: Record<string, unknown>): { email?: string } => ({
    email: typeof search.email === 'string' ? search.email : undefined,
  }),
});

// Приглашённый без имени в приглашении: бэкенд шлёт его на /onboarding/profile
// (`invitations.py`), экрана с таким адресом никогда не существовало. Пока
// отдельного шага «заполните профиль» нет в дизайне, ведём в мастер первого
// входа — он и так следующий шаг для новичка. Заводить здесь выдуманный экран
// без DESIGN-LEAD не буду: это ровно то, что запрещает UI-first протокол.
const onboardingProfileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/onboarding/profile',
  component: function OnboardingProfileRedirect() {
    return <Navigate to="/onboarding/first-run" replace />;
  },
});

// AK-584 — мастер первого входа (хозяйство → первое поле). Standalone-экран,
// как «Придумайте пароль»; `next=…` — куда вернуться после/при пропуске.
const firstRunRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/onboarding/first-run',
  component: FirstRunWizardPage,
  validateSearch: (search: Record<string, unknown>): { next?: string } => ({
    next: typeof search.next === 'string' ? search.next : undefined,
  }),
});

// AK-350 — «Управление компанией»: единый хаб админ-фич.
// Вкладки — сегмент URL (/company/<tab>); /company (index) — лендинг-обзор.
const companyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/company',
  component: CompanyPage,
});

const companyTabRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/company/$tab',
  component: function CompanyTabRouteComponent() {
    const { tab } = companyTabRoute.useParams();
    return <CompanyTabPage tab={tab} />;
  },
});

// AK-350 — старые URL редиректят внутрь раздела «Компания» (закладки не ломаются).
// AK-341 «Команда» → /company/team, AK-345 «Структура» → /company/structure.
const teamRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/team',
  component: function TeamRedirect() {
    return <Navigate to="/company/$tab" params={{ tab: 'team' }} replace />;
  },
});

const structureRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/structure',
  component: function StructureRedirect() {
    return (
      <Navigate to="/company/$tab" params={{ tab: 'structure' }} replace />
    );
  },
});

// AK-899 — раздел «Данные хозяйства» (макет v2, founder 08.10): бывшая
// карточка хозяйства (AK-746) + флоу «Подготовка хозяйства» (AK-772). Вкладка
// живёт в адресе: F5, «назад» и пересланная ссылка возвращают человека туда,
// где он был. `subtab` — под-вкладка (AK-747), `issue` — сигнал «здоровья
// данных», с которым раздел открыли извне, `action` — с чем открыли (AK-804:
// `import` — мастер загрузки парка). Правила разбора — `farmDataSearch`.
//
// `key={farmId}`: смена хозяйства (в переключателе вверху меню) — это другой экран, а
// не правка того же: выбор полей, фильтр и поиск прежнего хозяйства не должны
// переехать на новое.
const farmDataRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/farm/$farmId',
  component: function FarmDataRouteComponent() {
    const { farmId } = farmDataRoute.useParams();
    const { tab, subtab, issue, action } = farmDataRoute.useSearch();
    return (
      <FarmCardPage
        key={farmId}
        farmId={farmId}
        tab={tab}
        subtab={subtab}
        issue={issue}
        action={action}
      />
    );
  },
  validateSearch: farmDataSearch,
});

// Старые адреса раздела — только редиректы (AK-899). Закладки, пересланные
// ссылки и ссылки из других экранов открывают тот же экран на той же вкладке.
// Карточка хозяйства: все параметры переезжают как есть.
const structureFarmCardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/structure/farms/$farmId',
  component: function LegacyFarmCardRedirect() {
    const { farmId } = structureFarmCardRoute.useParams();
    const search = structureFarmCardRoute.useSearch();
    return <FarmDataRedirect farmId={farmId} search={search} />;
  },
  validateSearch: farmDataSearch,
});

// «Подготовка хозяйства» (`/farm?farm=`) и пункт меню «Данные хозяйства»
// (`/farm`, AK-898): хозяйство из адреса, иначе из области сессии
// (переключатель «Хозяйство ▾» вверху меню).
const farmEntryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/farm',
  component: function FarmEntryRouteComponent() {
    const { farm } = farmEntryRoute.useSearch();
    return <FarmEntryPage farmSearch={farm} />;
  },
  validateSearch: legacyFarmSearch,
});

// Матрица истории культур (`/farm/history?farm=`) стала вкладкой раздела.
// Статический сегмент старше `$farmId` — адрес не примут за хозяйство «history».
const farmCropHistoryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/farm/history',
  component: function FarmHistoryRedirect() {
    const { farm } = farmCropHistoryRoute.useSearch();
    return <FarmEntryPage farmSearch={farm} tab="history" />;
  },
  validateSearch: legacyFarmSearch,
});

const crmListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm',
  component: CrmListPage,
});

// Экран «Клиенты» консультанта (UC-3). Отдельный путь от /crm: там журнал
// активностей по РУЧНЫМ карточкам, здесь — список из внешней системы продаж
// с деревом отделов. Гейт по типу компании — на бэке (404 для агро).
export const consultantClientsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/clients',
  component: ConsultantClientsPage,
  // Состояние списка живёт в адресе: страница, поиск, отдел, фильтр. Ссылка
  // на найденного клиента отправляется коллеге, «назад» возвращает туда, где
  // человек был, а не в начало пула из 11 тысяч (AK-627).
  //
  // Числа — ЧИСЛАМИ, флаг — числом 1: строковая '1' сериализуется
  // TanStack-роутером с JSON-кавычками (`inSystem=%221%22`) и обратно уже не
  // узнаётся (та же грабля, что на /map — см. validateSearch карты).
  validateSearch: (
    search: Record<string, unknown>,
  ): { page?: number; q?: string; node?: string; inSystem?: 1 } => {
    const page = Number(search.page);
    const q = typeof search.q === 'string' ? search.q.trim() : '';
    return {
      page: Number.isInteger(page) && page > 1 ? page : undefined,
      q: q || undefined,
      node: typeof search.node === 'string' && search.node ? search.node : undefined,
      inSystem: search.inSystem === 1 || search.inSystem === '1' ? 1 : undefined,
    };
  },
});

// Карточка клиента консультанта (AK-979): вкладка — в адресе, чтобы ссылку
// на «Продажи» клиента можно было отправить коллеге. «Обзор» в адрес не пишем.
export const consultantClientCardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/clients/$externalId',
  component: function ConsultantClientCardRouteComponent() {
    const { externalId } = consultantClientCardRoute.useParams();
    return <ConsultantClientCardPage externalId={externalId} />;
  },
  validateSearch: (search: Record<string, unknown>): { tab?: ClientCardTab } => ({
    tab: parseClientCardTab(search.tab),
  }),
});

const crmNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/new',
  component: CrmNewPage,
});

const crmDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/$companyId',
  component: function CrmDetailRouteComponent() {
    const { companyId } = crmDetailRoute.useParams();
    return <CrmDetailPage companyId={companyId} />;
  },
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  scienceRoute,
  scienceSchemeNewRoute,
  scienceSchemeRoute,
  scienceSchemeEditRoute,
  sciencePlaceRoute,
  scienceTrialRoute,
  dashboardRoute,
  fieldsListRoute,
  fieldsNewRoute,
  fieldsDetailRoute,
  planningRoute,
  planningSppOverviewRoute,
  planningSeasonBudgetRoute,
  planningSalePricesRoute,
  planningBudgetCatalogsRoute,
  planningBudgetTargetsRoute,
  planningTechCardsRoute,
  planVariantRoute,
  planVariantSummaryRoute,
  programCompareRoute,
  planVariantFeasibilityRoute,
  planningCropStructureRoute,
  planningReadinessRoute,
  planningSowingPlanRoute,
  planningYieldNormsRoute,
  planningSystemsRoute,
  planningSystemEditorRoute,
  analyticsHistoryRoute,
  seasonClosingRoute,
  mapRoute,
  loginRoute,
  twoFARequiredRoute,
  twoFAChallengeRoute,
  selectTenantRoute,
  selectContextRoute,
  acceptInviteRoute,
  tenantSuspendedRoute,
  operationsRoute,
  operationDetailRoute,
  onboardingCompanyRoute,
  firstRunRoute,
  setPasswordOnboardingRoute,
  gettingStartedRoute,
  consultantProtosRoute,
  platformAdminDashboardRoute,
  platformAdminCompaniesRoute,
  platformAdminUsersRoute,
  platformAdminAdminsRoute,
  platformAdminCropsRoute,
  platformAdminCropCategoriesRoute,
  platformAdminCropGroupsRoute,
  platformAdminGrowingGoalsRoute,
  platformAdminDiseasesRoute,
  platformAdminPestsRoute,
  platformAdminWeedsRoute,
  platformAdminDiseaseCategoriesRoute,
  platformAdminFieldStatesRoute,
  platformAdminLandCategoriesRoute,
  platformAdminPestCategoriesRoute,
  platformAdminWeedCategoriesRoute,
  platformAdminWeedLifeCyclesRoute,
  platformAdminOperationTypesRoute,
  platformAdminMachineryRoute,
  platformAdminMachineryTypesRoute,
  platformAdminManufacturersRoute,
  platformAdminUnitsRoute,
  platformAdminCropQueueRoute,
  platformAdminVarietiesRoute,
  platformAdminPesticidesRoute,
  pesticidesCatalogRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  authErrorRoute,
  linkSentRoute,
  onboardingProfileRoute,
  companyRoute,
  companyTabRoute,
  teamRoute,
  structureRoute,
  structureFarmCardRoute,
  farmDataRoute,
  farmEntryRoute,
  farmCropHistoryRoute,
  consultantClientsRoute,
  consultantClientCardRoute,
  crmListRoute,
  crmNewRoute,
  crmDetailRoute,
]);

export const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
